<template>
  <!-- ONE MULTI, ONE CARD. At rest it says only what a punter reads before
       deciding: the book, the legs, the tier, the price, the return, the one
       chance line, and each leg as name, event and time, price. Why each leg is
       there, the fair price and the editing are one tap away (Details, Edit
       legs), and open in place. One filled action: go to the book. -->
  <article class="sheet ask-ticket" :class="{ 'is-selectable': selectable }" :aria-labelledby="`${id}-price`">
    <header class="head">
      <!-- CHOOSING WHAT TO SHARE: a 44px check at the top-left, drawn inline
           (no IonIcon in list rows), and the ticket's own buttons put away. -->
      <button
        v-if="selectable"
        type="button"
        class="pick"
        role="checkbox"
        :aria-checked="selected ? 'true' : 'false'"
        :aria-label="`Choose the ${money(ticket.multi.combinedOdds)} multi`"
        @click="emit('toggle-select', ticket)"
      >
        <svg class="pick-mark" viewBox="0 0 22 22" aria-hidden="true">
          <circle v-if="!selected" cx="11" cy="11" r="10" fill="none" stroke="currentColor" stroke-width="1.5" />
          <template v-else>
            <circle cx="11" cy="11" r="11" fill="currentColor" />
            <path d="M6.5 11.5l3 3 6-6.5" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </template>
        </svg>
      </button>
      <p class="book">{{ bookName }} · {{ legCount }}</p>
      <span v-if="stamp" class="stamp stamp--small ask-stamp" :class="stamp.cls">{{ stamp.word }}</span>
      <p class="tier" :style="{ color: ink }">{{ tierLabel }}</p>
    </header>

    <div class="money">
      <p :id="`${id}-price`" class="odds" :aria-label="`Pays ${money(ticket.multi.combinedOdds)}`">
        ${{ price(ticket.multi.combinedOdds) }}
      </p>
      <p class="returns">Returns {{ money(ticket.multi.potentialReturn) }}</p>
    </div>
    <p class="chance">{{ chance }}</p>
    <p v-if="details && valueLine" class="value">{{ valueLine }}</p>

    <ol class="legs" aria-label="Legs">
      <li v-for="leg in ticket.legs" :key="leg.key" class="leg">
        <div class="leg-main">
          <div class="leg-words">
            <p class="leg-name">
              <Glyph v-if="leg.locked" class="leg-lock" name="lock" /><span v-if="leg.locked" class="sr-only">Locked: </span>{{ bare(leg.selection) }}
            </p>
            <p class="leg-meta">
              {{ leg.sportTitle }} · {{ when(leg.commenceTime) }}<template v-if="startedKeys.has(leg.key)"> · started</template>
            </p>
          </div>
          <p class="leg-price">{{ price(leg.price) }}</p>
        </div>
        <p v-if="details && leg.rationale" class="reason">{{ leg.rationale }}</p>

        <div v-if="editing && !selectable" class="leg-tools">
          <button
            type="button"
            class="tbtn tool"
            :aria-pressed="leg.locked ? 'true' : 'false'"
            :aria-label="`${leg.locked ? 'Unlock' : 'Lock'} ${bare(leg.selection)}`"
            @click="emit('lock', leg.key)"
          >
            <Glyph :name="leg.locked ? 'unlock' : 'lock'" />
            {{ leg.locked ? 'Unlock' : 'Lock' }}
          </button>
          <button
            v-if="!leg.locked"
            type="button"
            class="tbtn tool"
            :aria-expanded="swappingKey === leg.key ? 'true' : 'false'"
            :aria-controls="`${id}-swap-${leg.side}`"
            :aria-label="`Swap ${bare(leg.selection)}`"
            @click="emit('swap', ticket, leg.key)"
          >
            <Glyph name="swap" />
            Swap
          </button>
        </div>

        <!-- THE SWAP OPENS HERE, under the leg it replaces, and says which. -->
        <div v-if="editing && !selectable && swappingKey === leg.key" :id="`${id}-swap-${leg.side}`" class="swap" role="group" :aria-label="`Instead of ${bare(leg.selection)}`">
          <p class="swap-title">Instead of {{ bare(leg.selection) }}</p>
          <p v-if="!swap.options.length" class="swap-empty">{{ swap.empty }}</p>
          <button
            v-for="o in swap.options"
            :key="o.multi.id"
            type="button"
            class="swap-option"
            @click="emit('use-swap', o)"
          >
            <span class="swap-line">
              <span class="swap-name">{{ bare(swap.newLeg(o).selection) }}</span>
              <span class="swap-price">{{ price(swap.newLeg(o).price) }}</span>
            </span>
            <span class="swap-sub">{{ swap.newLeg(o).sportTitle }} · {{ when(swap.newLeg(o).commenceTime) }} · multi pays ${{ price(o.multi.combinedOdds) }}</span>
          </button>
          <button type="button" class="tbtn swap-cancel" @click="emit('cancel-swap')">Keep {{ bare(leg.selection) }}</button>
        </div>
      </li>
    </ol>

    <template v-if="details && !selectable">
      <p v-if="honouredLine" class="note">{{ honouredLine }}</p>
      <p v-if="relaxedLine" class="note note--loosened">{{ relaxedLine }}</p>
      <p class="line">
        <button type="button" class="tbtn" :aria-pressed="pinned ? 'true' : 'false'" @click="emit('pin', ticket)">
          {{ pinned ? 'Pinned' : 'Pin it' }}
        </button>
        <button type="button" class="tbtn" @click="emit('share', ticket)">Share</button>
        <button type="button" class="tbtn tbtn--quiet" @click="emit('dismiss', ticket)">Not this one</button>
      </p>
    </template>

    <footer v-if="!selectable" class="actions">
      <!-- A started leg can no longer be written: said, not greyed out. -->
      <p v-if="started && !placed" class="off-note">A leg has started, so this multi can no longer be placed.</p>
      <button
        v-else-if="book"
        type="button"
        class="open"
        :class="{ 'open--placed': placed }"
        :disabled="opening"
        @click="openBook"
      >{{ openLabel(book, target) }}</button>
      <p class="line">
        <button v-if="placed" type="button" class="tbtn" @click="goHistory">In History ›</button>
        <button v-else-if="!started" type="button" class="tbtn" @click="emit('place', ticket)">Record bet</button>
        <button type="button" class="tbtn" :aria-expanded="details ? 'true' : 'false'" @click="details = !details">Details</button>
        <button type="button" class="tbtn" :aria-pressed="editing ? 'true' : 'false'" @click="toggleEdit">{{ editing ? 'Done' : 'Edit legs' }}</button>
      </p>
    </footer>
  </article>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import Glyph from './Glyph.vue';
import { bookmakerName } from '../../lib/odds.js';
import { TIER_LABEL, tierInk, money, price, chanceLine, oneIn, signedPct, isStale } from '../../lib/format.js';
import {
  handoffBook, openLabel, openTarget, rememberOpened, slipCopiedLine,
} from '../../lib/bookmakers.js';
import { copyText, COPY_FAILED } from '../../lib/clipboard.js';
import { slipText } from '../../lib/tickets.js';
import { placedIds, riskProfile } from '../../store.js';
import { toast } from '../../core/ui';
import { haptic } from '../../core/util.js';
import { openExternal } from '../../core/native.js';

const props = defineProps({
  ticket: { type: Object, required: true },
  pinned: { type: Boolean, default: false },
  /** The leg whose swap is open on this ticket, or null. */
  swappingKey: { type: String, default: null },
  /** { options, empty, newLeg(option) } while a swap is open. */
  swap: { type: Object, default: () => ({ options: [], empty: '', newLeg: (o) => o.legs[0] }) },
  /** The clock, so a leg that starts while the list is open is marked. */
  now: { type: Number, default: () => Date.now() },
  /** The list is choosing what to share: a check shows, the buttons go. */
  selectable: { type: Boolean, default: false },
  /** This ticket is one of the chosen. */
  selected: { type: Boolean, default: false },
});
const emit = defineEmits([
  'swap', 'use-swap', 'cancel-swap', 'pin', 'place', 'copy', 'dismiss', 'lock', 'share', 'toggle-select',
]);

const id = computed(() => `t-${props.ticket.multi.id}`);
const details = ref(false);
const editing = ref(false);
function toggleEdit() {
  editing.value = !editing.value;
  if (!editing.value && props.swappingKey) emit('cancel-swap');
}

// The list holds each ticket as it was worked out; the world moves on under it.
const placed = computed(() => placedIds.value.has(props.ticket.multi.id));
const startedKeys = computed(() => new Set(props.ticket.legs
  .filter((l) => Date.parse(l.commenceTime) <= props.now).map((l) => l.key)));
const started = computed(() => startedKeys.value.size > 0);
const stamp = computed(() => {
  if (placed.value) return { word: 'Placed', cls: 'stamp--placed' };
  if (started.value) return { word: 'Started', cls: '' };
  if (isStale(props.ticket.multi.oddsFetchedAt, props.now)) return { word: 'Stale', cls: '' };
  return null;
});
// One spelling per book: the sports feed says "SportsBet" and the racing feed
// "Sportsbet", and a list of the same shop in two spellings reads as two shops.
const bookName = computed(() => {
  const key = props.ticket.multi.bookmakerKey;
  const known = bookmakerName(key);
  return known && known !== key ? known : props.ticket.multi.bookmaker;
});
const ink = computed(() => tierInk(props.ticket.multi.tier));
const tierLabel = computed(() => TIER_LABEL[props.ticket.multi.tier] ?? '');
const legCount = computed(() => `${props.ticket.legs.length} leg${props.ticket.legs.length === 1 ? '' : 's'}`);
const chance = computed(() => (props.ticket.legs.length > 1
  ? chanceLine(props.ticket.multi.combinedProbability)
  : oneIn(props.ticket.multi.combinedProbability)));

/** The ticket's value against the market, in dollars first: "Fair $3.58 · pays $3.21 (−10.4%)". */
const valueLine = computed(() => {
  const p = props.ticket.multi.combinedProbability;
  const pays = props.ticket.multi.combinedOdds;
  if (!(p > 0) || !(pays > 1)) return '';
  return `Fair ${money(1 / p)} · pays ${money(pays)} (${signedPct(pays * p - 1)})`;
});

const when = (iso) => new Date(iso).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' }).replace(' ', '').toLowerCase();
const bare = (selection) => String(selection ?? '').replace(/ to win$/, '');

const honouredLine = computed(() => (props.ticket.multi.honoured ?? []).map((h) => h.text).join(' · '));
const relaxedLine = computed(() => {
  const r = props.ticket.multi.relaxed ?? [];
  if (!r.length) return '';
  const said = r.map((x) => {
    if (x.dimension === 'legs') return `${x.to} leg${x.to === 1 ? '' : 's'} instead of ${x.from.min === x.from.max ? x.from.min : `${x.from.min}–${x.from.max}`}`;
    if (x.dimension === 'risk') return 'any risk';
    if (x.dimension === 'when') return `${x.to} instead of ${x.from}`;
    return x.dimension;
  });
  return `Loosened: ${said.join(', ')} (${r[0].why}).`;
});

// --- the handoff ------------------------------------------------------------------
// The same act TicketActions performs (lib/bookmakers.js decides which book,
// where the tap goes and what the button says), off this ticket's own legs.
const book = computed(() => handoffBook(props.ticket.multi, riskProfile.value?.bookmaker));
const target = computed(() => openTarget(book.value, props.ticket.legs));
const opening = ref(false);

/** Copy the slip, say so, leave. The copy first: it must start inside the tap. */
async function openBook() {
  const b = book.value;
  if (!b || opening.value) return;
  const to = target.value;
  opening.value = true;
  haptic('tick');
  try {
    await copyText(slipText(props.ticket.multi, props.ticket.legs));
    toast(slipCopiedLine(b, to));
  } catch (e) {
    toast(e?.message ? String(e.message) : COPY_FAILED);
  }
  // So the Place alert can say "Opened Neds" rather than its usual line.
  rememberOpened(props.ticket.multi.id, b);
  try {
    await openExternal(to.url);
  } finally {
    opening.value = false;
  }
}

const router = useRouter();
const goHistory = () => router.push({ name: 'history', query: { tab: 'pending' } });
</script>

<style scoped>
.ask-ticket {
  padding: 14px 16px 2px;
  border: 0;
  box-shadow: none;
}
:where(.ask-ticket) p {
  margin: 0;
}
.head {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
}
/* The check: a 44px target at the ticket's top-left, pulled into the card's
   padding so the row does not grow. */
.pick {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  margin: -12px 0 -12px -12px;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--carbon-faint);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.pick[aria-checked='true'] {
  color: var(--accent);
}
.pick-mark {
  width: 22px;
  height: 22px;
}
.is-selectable {
  padding-bottom: 14px;
}
.book {
  flex: 1 1 auto;
  min-width: 0;
  font: 400 0.8125rem/1.3 var(--font-body);
  color: var(--carbon-faint);
}
.ask-stamp {
  flex: 0 0 auto;
}
.tier {
  flex: 0 0 auto;
  font: 600 0.8125rem/1.3 var(--font-body);
}
.money {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--sp-3);
  margin-top: 4px;
}
.odds {
  font: 700 var(--fs-odds)/1 var(--font-print);
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
  color: var(--carbon);
}
.returns {
  font: 400 0.9375rem/1.2 var(--font-body);
  font-variant-numeric: tabular-nums;
  color: var(--carbon-faint);
  text-align: right;
}
.chance {
  margin-top: 4px;
  font: 400 0.9375rem/1.3 var(--font-body);
  color: var(--carbon);
}
.value {
  margin-top: 2px;
  font: 400 0.8125rem/1.3 var(--font-body);
  font-variant-numeric: tabular-nums;
  color: var(--carbon-faint);
}

/* THE LEGS: one row each, an inset hairline between. */
.legs {
  margin: 8px 0 0;
  padding: 0;
  list-style: none;
}
.leg {
  padding: 7px 0;
  border-top: 1px solid var(--rule);
}
.leg-main {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-3);
}
.leg-words {
  min-width: 0;
}
.leg-name {
  font: 600 0.9375rem/1.25 var(--font-body);
  color: var(--carbon);
}
.leg-lock {
  width: 12px;
  height: 12px;
  margin-right: 4px;
  vertical-align: -1px;
  color: var(--carbon-faint);
}
.leg-meta {
  margin-top: 1px;
  font: 400 0.8125rem/1.25 var(--font-body);
  color: var(--carbon-faint);
}
.leg-price {
  flex: 0 0 auto;
  font: 600 0.9375rem/1.25 var(--font-print);
  font-variant-numeric: tabular-nums;
  color: var(--carbon);
}
.reason {
  margin-top: 4px;
  font: 400 0.8125rem/1.35 var(--font-body);
  color: var(--carbon-faint);
}
.leg-tools {
  display: flex;
  gap: var(--sp-4);
}
.tool {
  gap: 6px;
  font-size: 0.9375rem;
}
.tool[aria-pressed='true'] {
  font-weight: 600;
}

/* The swap, under its leg: tonal rows, no boxes. */
.swap {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 4px 0 2px;
}
.swap-title {
  font: 600 0.8125rem/1.3 var(--font-body);
  color: var(--carbon-faint);
}
.swap-empty {
  font: 400 0.8125rem/1.35 var(--font-body);
  color: var(--carbon-faint);
}
.swap-option {
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
  min-height: 44px;
  padding: 8px 12px;
  border: 0;
  border-radius: var(--r-control);
  background: var(--fill, var(--rule));
  color: var(--carbon);
  font: inherit;
  text-align: left;
  cursor: pointer;
}
.swap-line {
  display: flex;
  justify-content: space-between;
  gap: var(--sp-2);
}
.swap-name {
  font: 600 0.9375rem/1.3 var(--font-body);
}
.swap-price {
  font: 600 0.9375rem/1.3 var(--font-print);
  font-variant-numeric: tabular-nums;
}
.swap-sub {
  font: 400 0.8125rem/1.3 var(--font-body);
  color: var(--carbon-faint);
}
.swap-cancel {
  align-self: flex-start;
  font-size: 0.9375rem;
}

.note {
  margin-top: 6px;
  font: 400 0.8125rem/1.35 var(--font-body);
  color: var(--carbon-faint);
}
.note--loosened {
  color: var(--tier-medium);
}

/* --- actions ------------------------------------------------------------------- */
.actions {
  margin-top: 6px;
}
.open {
  display: block;
  width: 100%;
  min-height: 44px;
  padding: 0 16px;
  border: 0;
  border-radius: var(--r-action);
  background: var(--go);
  color: var(--sheet);
  font: 600 var(--fs-body)/1.2 var(--font-body);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.open--placed {
  background: var(--fill, var(--rule));
  color: var(--accent);
}
.open:disabled {
  opacity: 0.6;
}
.off-note {
  padding: 8px 0;
  font: 400 0.8125rem/1.35 var(--font-body);
  color: var(--carbon-faint);
}
.line {
  display: flex;
  flex-wrap: wrap;
  column-gap: var(--sp-4);
}
.tbtn {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--accent);
  font: 400 0.9375rem/1.2 var(--font-body);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.tbtn--quiet {
  color: var(--carbon-faint);
}
.tbtn[aria-expanded='true'] {
  font-weight: 600;
}
.tbtn:focus-visible,
.pick:focus-visible,
.open:focus-visible,
.swap-option:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}
</style>
